import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from '../../axiosConfig/axiosConfig';
import SideBarPaciente from './SideBarPaciente';
import { clearAuthSession, getAuthValue } from '../../lib/authSession';
import './InicioPaciente.scss';

const ICONOS_POR_TIPO = {
  nuevo_turno: 'bi-calendar-plus',
  turno_reservado_profesional: 'bi-person-check',
  datos_actualizados: 'bi-pencil-square',
  recordatorio_turno: 'bi-bell',
};

const LABELS_POR_TIPO = {
  nuevo_turno: 'Nuevo turno',
  turno_reservado_profesional: 'Reservado por profesional',
  datos_actualizados: 'Datos actualizados',
  recordatorio_turno: 'Recordatorio',
};

const InicioPaciente = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userName, setUserName] = useState(() => {
    const n = getAuthValue('userName') || '';
    const a = getAuthValue('userApellido') || '';
    return [n, a].filter(Boolean).join(' ') || n || 'Usuario';
  });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotificaciones, setShowNotificaciones] = useState(false);
  const [showSidebarMovil, setShowSidebarMovil] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const userMenuRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    setShowSidebarMovil(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = () => {
      const n = getAuthValue('userName') || '';
      const a = getAuthValue('userApellido') || '';
      setUserName([n, a].filter(Boolean).join(' ') || n || 'Usuario');
    };
    window.addEventListener('userNameUpdated', handler);
    return () => window.removeEventListener('userNameUpdated', handler);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotificaciones(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showNotificaciones) return;
    let cancelled = false;
    setNotifLoading(true);
    axios.get('/notificaciones-paciente')
      .then((res) => {
        if (!cancelled) setNotificaciones(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => { if (!cancelled) setNotificaciones([]); })
      .finally(() => { if (!cancelled) setNotifLoading(false); });
    return () => { cancelled = true; };
  }, [showNotificaciones]);

  const handleLogout = async () => {
    setShowUserMenu(false);
    try {
      await axios.post('/auth/logout');
    } catch (_err) {
      // Si falla el backend, igual cerramos sesión en cliente.
    }
    clearAuthSession();
    window.location.href = '/inicio';
  };

  const formatNotifFecha = (fechaStr) => {
    if (!fechaStr) return '';
    try {
      const d = new Date(fechaStr);
      const hoy = new Date();
      const esHoy = d.toDateString() === hoy.toDateString();
      if (esHoy) return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
      return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const marcarNotifLeida = (id) => {
    axios.put(`/notificaciones-paciente/${id}/leer`).then(() => {
      setNotificaciones((prev) => prev.map((n) => (n.id === id ? { ...n, leida: true } : n)));
    }).catch(() => {});
  };

  return (
    <div className={`inicio-paciente ${showSidebarMovil ? 'inicio-paciente--menu-open' : ''}`}>
      <header className="header-paciente">
        <div className="header-paciente__left">
          <button
            type="button"
            className="header-paciente__menu-btn d-lg-none"
            onClick={() => setShowSidebarMovil(true)}
            aria-label="Abrir menú"
          >
            <i className="bi bi-list" />
          </button>
          <Link to="/inicio" className="logo-paciente" style={{ textDecoration: 'none', color: 'inherit' }}>
            Ridens
          </Link>
        </div>
        <div className="user-badge-wrapper">
          <div className="user-badge-notif-anchor" ref={notifRef}>
            {showNotificaciones && (
              <div className="user-notificaciones-dropdown">
                <div className="user-notificaciones-dropdown__header">Notificaciones</div>
                {notifLoading ? (
                  <div className="user-notificaciones-dropdown__loading">Cargando...</div>
                ) : notificaciones.length === 0 ? (
                  <div className="user-notificaciones-dropdown__empty">No tenés notificaciones</div>
                ) : (
                  <div className="user-notificaciones-dropdown__list">
                    {notificaciones.slice(0, 5).map((n) => (
                      <div
                        key={n.id}
                        className={`user-notificaciones-dropdown__item ${!n.leida ? 'user-notificaciones-dropdown__item--no-leida' : ''}`}
                        onClick={() => !n.leida && marcarNotifLeida(n.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => !n.leida && (e.key === 'Enter' || e.key === ' ') && marcarNotifLeida(n.id)}
                      >
                        <i className={`bi ${ICONOS_POR_TIPO[n.tipo] || 'bi-bell'}`} />
                        <div className="user-notificaciones-dropdown__item-content">
                          <span className="user-notificaciones-dropdown__item-titulo">{n.titulo}</span>
                          {n.mensaje && <span className="user-notificaciones-dropdown__item-mensaje">{n.mensaje}</span>}
                          <span className="user-notificaciones-dropdown__item-fecha">{formatNotifFecha(n.created_at)}</span>
                        </div>
                        {!n.leida && <span className="user-notificaciones-dropdown__dot" />}
                      </div>
                    ))}
                  </div>
                )}
                {!notifLoading && notificaciones.length > 0 && (
                  <Link
                    to="/inicio/notificaciones"
                    className="user-notificaciones-dropdown__ver-todas"
                    onClick={() => setShowNotificaciones(false)}
                  >
                    Ver todas
                  </Link>
                )}
              </div>
            )}
            <button
              type="button"
              className="user-badge user-badge--notif"
              onClick={(e) => {
                e.stopPropagation();
                setShowNotificaciones((v) => !v);
                setShowUserMenu(false);
              }}
              aria-expanded={showNotificaciones}
              aria-label="Ver notificaciones"
              title="Notificaciones"
            >
              <i className={`bi bi-bell-fill ${showNotificaciones ? 'user-badge__notif-icon--activo' : ''}`} />
            </button>
          </div>
          <div className="user-badge-menu-anchor" ref={userMenuRef}>
            <button
              type="button"
              className="user-badge"
              onClick={() => {
                setShowUserMenu((v) => !v);
                setShowNotificaciones(false);
              }}
              aria-expanded={showUserMenu}
              aria-haspopup="true"
            >
              <span className="user-badge__nombre" title={userName}>{userName}</span>
              <i className={`bi bi-chevron-down ${showUserMenu ? 'rotated' : ''}`} style={{ fontSize: '0.9rem', transition: 'transform 0.2s' }} />
            </button>
            {showUserMenu && (
            <div className="user-badge-dropdown">
              <button
                type="button"
                className="user-badge-dropdown__item"
                onClick={() => { setShowUserMenu(false); navigate('/inicio/configuracion_cuenta'); }}
              >
                <i className="bi bi-person" /> Mis datos
              </button>
              <button type="button" className="user-badge-dropdown__item" onClick={handleLogout}>
                <i className="bi bi-box-arrow-left" /> Cerrar sesión
              </button>
            </div>
            )}
          </div>
        </div>
      </header>
      {showSidebarMovil && (
        <button
          type="button"
          className="inicio-paciente__overlay d-lg-none inicio-paciente__overlay--open"
          onClick={() => setShowSidebarMovil(false)}
          aria-label="Cerrar menú"
        />
      )}
      <div className="body-paciente">
        <aside className="sideBar-wrapper">
          <SideBarPaciente
            onClose={() => setShowSidebarMovil(false)}
            showCloseOnMovil={showSidebarMovil}
          />
        </aside>
        <main className="content-paciente">
          {children}
        </main>
      </div>
    </div>
  );
};

export default InicioPaciente;
